import { Typography } from "@mui/material";
import { Column, ColumnDef, Row } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { ArrowUpDown } from "lucide-react";

import { useIsMobile } from "@/hooks/use-mobile";

export type UpdateColumns = {
  processedDate: Date;
  fileName: string;
  batchId: number;
  fileType: string;
};

export function useUpdatesColumns(): ColumnDef<UpdateColumns>[] {
  const isMobile = useIsMobile();
  const columns: ColumnDef<UpdateColumns>[] = [
    {
      accessorKey: "fileName",
      header: ({ column }: { column: Column<UpdateColumns> }) => {
        return (
          <Button
            variant="ghost"
            className="pl-0 ml-0 hover:bg-gray-200"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              Nombre del Archivo
            </Typography>
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row: { original: update } }: { row: Row<UpdateColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[200px]"
            aria-label="nombre-archivo"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {update.fileName}
            </Typography>
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "fileType",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Tipo de Archivo
          </Typography>
        </div>
      ),
      cell: ({ row: { original: update } }: { row: Row<UpdateColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[120px]"
            aria-label="tipo-archivo"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {update.fileType}
            </Typography>
          </div>
        );
      },
    },
    !isMobile && {
      accessorKey: "batchId",
      header: ({ column }: { column: Column<UpdateColumns> }) => {
        return (
          <Button
            variant="ghost"
            className="pl-0 ml-0 hover:bg-gray-200"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              ID de Lote
            </Typography>
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row: { original: update } }: { row: Row<UpdateColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[100px]"
            aria-label="id-lote"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {update.batchId}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "processedDate",
      header: ({ column }: { column: Column<UpdateColumns> }) => {
        return (
          <Button
            variant="ghost"
            className="pl-0 ml-0 hover:bg-gray-200"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              Fecha de Procesamiento
            </Typography>
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row: { original: update } }: { row: Row<UpdateColumns> }) => {
        const formattedDate = new Date(update.processedDate).toLocaleDateString(
          "es-ES",
          {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          },
        );

        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="fecha-procesamiento"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {formattedDate}
            </Typography>
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnDef<UpdateColumns>[];
  return columns;
}
